import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, X, Send, Sparkles, Trash2, Paperclip, FileText, Camera, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Floating bottom-right button the user can click to flag a problem.
 * Types free-text → LLM extracts structured metadata → stored as a "memory".
 * Memories are then surfaced as contextual notices on related client/product pages.
 */
export default function AIFeedbackButton({ inHeader = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Pre-submit staging: users can pick attachments BEFORE saving the memory.
  // After the memory is created, these files are uploaded in sequence.
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingPending, setUploadingPending] = useState(false);

  const addPendingFile = (file) => {
    if (!file) return;
    if (pendingFiles.length + (result?.attachments?.length || 0) >= 5) {
      toast.error('Max 5 attachments'); return;
    }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setPendingFiles(prev => [...prev, file]);
  };

  const removePending = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ai-memory`, { text: text.trim() }, { withCredentials: true });
      setResult(res.data);
      toast.success('AI memory saved — will surface on related pages');
      // If user staged files while typing, upload them now
      if (pendingFiles.length > 0) {
        setUploadingPending(true);
        const uploaded = [];
        for (const f of pendingFiles) {
          const fd = new FormData(); fd.append('file', f);
          try {
            const r = await axios.post(`${API}/ai-memory/${res.data.id}/attachments`, fd, {
              withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
            });
            uploaded.push(r.data);
          } catch (e) { toast.error(`${f.name}: ${e.response?.data?.detail || 'upload failed'}`); }
        }
        setResult(prev => ({ ...prev, attachments: [...(prev.attachments || []), ...uploaded] }));
        setPendingFiles([]);
        setUploadingPending(false);
        if (uploaded.length > 0) toast.success(`${uploaded.length} attachment(s) uploaded`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!result?.id) return;
    try {
      await axios.delete(`${API}/ai-memory/${result.id}`, { withCredentials: true });
      toast.success('Memory deleted');
      reset();
    } catch { toast.error('Delete failed'); }
  };

  const reset = () => { setText(''); setResult(null); setPendingFiles([]); };

  // Two render variants: in-header (compact icon next to theme toggle) or
  // legacy floating (no longer mounted globally, but preserved for backwards-compat).
  const triggerCls = inHeader
    ? `relative p-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-amber-400 transition-colors ${className}`
    : `fixed bottom-36 right-5 z-[60] h-11 w-11 rounded-full bg-amber-500 hover:bg-amber-400 text-black shadow-lg flex items-center justify-center transition-all hover:scale-110 ${className}`;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ai-feedback-button"
        title="Report an issue or grievance — the AI will remember it"
        className={triggerCls}>
        <AlertTriangle size={inHeader ? 16 : 16} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => { setOpen(false); reset(); }}>
          <div className="bg-zinc-950 border border-amber-500/40 rounded-2xl w-full max-w-xl p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="ai-feedback-modal">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-amber-400" />
                <h3 className="font-heading font-semibold text-zinc-50">Issue / Grievance Notice</h3>
              </div>
              <button onClick={() => { setOpen(false); reset(); }} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
            </div>

            {!result ? (
              <>
                <p className="text-zinc-400 text-xs">
                  Describe any issue, grievance or thing to be remembered. The AI will link it to the right
                  client / product / vendor and show a notice next time you open their page.
                </p>
                <textarea value={text} onChange={e => setText(e.target.value)} rows={4} autoFocus
                  placeholder="e.g. ACME Traders pays 20 days late · Chilli 12A overweight · Vendor XYZ delayed 3 POs in a row"
                  data-testid="ai-feedback-text"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />

                {/* Pre-submit attachment picker (photos / PDFs / docs) */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer hover:text-zinc-200 transition-colors" data-testid="ai-feedback-attach-label">
                    <div className="flex-1 border-2 border-dashed border-zinc-700 hover:border-amber-500 rounded-lg py-2.5 px-3 flex items-center justify-center gap-2">
                      <Paperclip size={14} className="text-zinc-500" />
                      <span className="text-[11px]">Attach a file · {(5 - pendingFiles.length - (result?.attachments?.length || 0))} slot(s) left · ≤10MB each</span>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      data-testid="ai-feedback-file-input"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) { addPendingFile(f); e.target.value = ''; } }}
                      accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    />
                  </label>
                  {pendingFiles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="ai-feedback-pending-list">
                      {pendingFiles.map((f, i) => (
                        <div key={i} className="flex items-center gap-1 bg-zinc-800 border border-zinc-700 rounded-md pl-2 pr-1 py-1">
                          <Paperclip size={10} className="text-amber-400" />
                          <span className="text-[10px] text-zinc-200 max-w-[140px] truncate" title={f.name}>{f.name}</span>
                          <button type="button" onClick={() => removePending(i)} className="text-zinc-500 hover:text-red-400 px-0.5" data-testid={`ai-feedback-remove-${i}`}>
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setOpen(false); reset(); }} className="px-4 py-2 text-xs bg-zinc-800 text-zinc-300 rounded-lg">Cancel</button>
                  <button onClick={submit} disabled={busy || !text.trim()} data-testid="ai-feedback-submit"
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-black rounded-lg disabled:opacity-50">
                    <Send size={12} /> {busy ? 'Analyzing…' : (uploadingPending ? 'Uploading…' : pendingFiles.length > 0 ? `Save + upload ${pendingFiles.length}` : 'Save Memory')}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-3" data-testid="ai-feedback-result">
                <div className="bg-zinc-900 rounded-lg p-3 border border-zinc-800">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">AI Summary</div>
                  <div className="text-zinc-100 text-sm">{result.summary}</div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <Tag label="Linked to" value={result.linked_entity_name ? `${result.linked_entity_type}: ${result.linked_entity_name}` : '—'} />
                  <Tag label="Category" value={result.category} />
                  <Tag label="Severity" value={result.severity} color={result.severity === 'high' ? 'red' : result.severity === 'medium' ? 'amber' : 'zinc'} />
                </div>
                <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-3">
                  <div className="text-[10px] text-emerald-300 uppercase tracking-wider mb-1 font-semibold">Suggested solution</div>
                  <div className="text-zinc-100 text-sm">{result.suggested_solution}</div>
                </div>
                <Attachments memoryId={result.id} attachments={result.attachments || []} onChange={(atts) => setResult({ ...result, attachments: atts })} />
                <div className="flex justify-between">
                  <button onClick={del} data-testid="ai-feedback-delete"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs bg-red-900/30 text-red-300 hover:bg-red-900/60 rounded-lg font-semibold">
                    <Trash2 size={12} /> Delete
                  </button>
                  <div className="flex gap-2">
                    <button onClick={reset} className="px-3 py-2 text-xs bg-zinc-800 text-zinc-300 rounded-lg">Add Another</button>
                    <button onClick={() => { setOpen(false); reset(); }} className="px-4 py-2 text-xs font-semibold bg-amber-500 text-black rounded-lg">Done</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Tag({ label, value, color = 'zinc' }) {
  const cls = {
    red: 'bg-red-900/30 border-red-700/50 text-red-300',
    amber: 'bg-amber-900/30 border-amber-700/50 text-amber-300',
    zinc: 'bg-zinc-900 border-zinc-800 text-zinc-300',
  }[color];
  return (
    <div className={`rounded-lg border p-2 ${cls}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-semibold capitalize">{value}</div>
    </div>
  );
}

export function Attachments({ memoryId, attachments, onChange, max = 5, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const list = attachments || [];

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (list.length >= max) { toast.error(`Max ${max} attachments`); return; }
    if (f.size > 4 * 1024 * 1024) { toast.error('Max 4 MB per file'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/ai-memory/${memoryId}/attachments`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange?.([...list, res.data]);
      toast.success('Attached');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Remove ${att.filename}?`)) return;
    try {
      await axios.delete(`${API}/ai-memory/attachments/${att.id}`, { withCredentials: true });
      onChange?.(list.filter(x => x.id !== att.id));
      toast.success('Removed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className={compact ? '' : 'bg-zinc-900 rounded-lg p-3 border border-zinc-800'}>
      {!compact && (
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Paperclip size={10} /> Attachments {list.length}/{max}
        </div>
      )}
      <div className="flex gap-1.5 flex-wrap">
        {list.map(a => {
          const isImage = (a.content_type || '').startsWith('image/');
          return (
            <div key={a.id} className="relative group">
              <button onClick={() => setPreview(a)} title={a.filename}
                data-testid={`mem-att-open-${a.id}`}
                className="w-10 h-10 rounded border border-zinc-700 bg-zinc-950 hover:border-amber-500 overflow-hidden flex items-center justify-center">
                {isImage
                  ? <img src={`${API}/ai-memory/attachments/${a.id}`} alt="" className="w-full h-full object-cover" />
                  : <FileText size={14} className="text-amber-400" />}
              </button>
              <button onClick={() => remove(a)} data-testid={`mem-att-del-${a.id}`}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100" title="Remove">
                <X size={9} className="text-white" />
              </button>
            </div>
          );
        })}
        {list.length < max && (
          <>
            <input ref={fileRef} type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx" onChange={upload} className="hidden" data-testid={`mem-att-input-${memoryId}`} />
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`mem-att-add-${memoryId}`}
              className="w-10 h-10 rounded border border-dashed border-zinc-700 hover:border-amber-500 text-zinc-500 hover:text-amber-400 flex items-center justify-center"
              title="Attach (max 4 MB · image / PDF / doc / xls)">
              {busy ? <RefreshCw size={12} className="animate-spin" /> : <Camera size={14} />}
            </button>
          </>
        )}
      </div>
      {preview && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="mem-att-preview">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-w-4xl max-h-full overflow-auto p-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 gap-3">
              <div>
                <div className="text-sm font-bold text-zinc-50">{preview.filename}</div>
                <div className="text-xs text-zinc-500">{preview.content_type} · {(preview.size / 1024).toFixed(1)} KB · uploaded by {preview.uploaded_by_name || preview.uploaded_by}</div>
              </div>
              <div className="flex gap-2">
                <a href={`${API}/ai-memory/attachments/${preview.id}`} target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold">Open</a>
                <button onClick={() => setPreview(null)} className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 text-xs"><X size={14} /></button>
              </div>
            </div>
            {(preview.content_type || '').startsWith('image/')
              ? <img src={`${API}/ai-memory/attachments/${preview.id}`} alt={preview.filename} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
              : <iframe title={preview.filename} src={`${API}/ai-memory/attachments/${preview.id}`} className="w-[80vw] h-[70vh] rounded-lg bg-white" />}
          </div>
        </div>
      )}
    </div>
  );
}
